import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadGenderDetails } from '../../../store/directusService';
import {
    clearBusinessLogicError,
    clearValidationErrors,
    updateParticipant
} from '../../../store/participantService';
import { getConfiguredItems, getObjFromId } from '../../../utils/directusFunctions';
import {
    ADDRESS_LINE_1_MAX_LENGTH,
    ADDRESS_LINE_2_MAX_LENGTH,
    ADDRESS_LINE_3_MAX_LENGTH,
    CITY_MAX_LENGTH,
    MAX_EMAIL,
    MAX_PHONE,
    MAX_POSTCODE
} from '../../../utils/formValidation/commonConstants';
import {
    CONTRACT_DETAILS_ERROR,
    GENDER_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import DateSelect from '../../formElements/DateSelect';
import SingleSelect from '../../formElements/SingleSelect';
import TextInputField from '../../formElements/TextInputField';
import FormActions from '../../ui/formActions/FormActions';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ScreenError from '../../ui/messageBanners/ScreenError';

import { validate } from './validation/validateGeneral';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from './styles/general.module.css';

const _MESSAGE = 'Participant primary details have been updated';

const General = ({ onClose, isDisabled, isUniqueIdEditDisabled }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        address1: '',
        address2: '',
        address3: '',
        city: '',
        postcode: '',
        emailAddress: '',
        primaryPhoneNumber: '',
        secondaryPhoneNumber: '',
        genderId: '',
        dateOfBirth: '',
        uniqueIdentifier: '',
        repeatUniqueIdentifier: ''
    };

    const [errors, setErrors] = useState({});
    const [initialStateOnEntry, setInitialStateOnEntry] = useState({});
    const [newEntry, setNewEntry] = useState(initialState);
    const [arrayGenders, setArrayGenders] = useState([]);
    const [participantContractDetails, setParticipantContractDetails] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const genderDetails = useSelector((state) => state.entities.directusService.genderDetails);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const validationErrors = useSelector(
        (state) => state.entities.participantService.validationErrors
    );
    const businessLogicError = useSelector(
        (state) => state.entities.participantService.businessLogicError
    );

    // USE EFFECTS
    useEffect(() => {
        genderDetails?.length < 1 && dispatch(loadGenderDetails());
        updateInitialEntry();
    }, []);

    useEffect(() => {
        if (genderDetails?.length > 0 && currentParticipant?.contractId) {
            setArrayGenders(getConfiguredItems(genderDetails, currentParticipant.contractId));
        }
    }, [genderDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (!(currentParticipant.contractId in contractDetailsById)) return;
        setParticipantContractDetails(contractDetailsById[currentParticipant.contractId]);
    }, [contractDetailsById[currentParticipant.contractId]]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _MESSAGE) {
            resetFields();
            onClose('general');
        }
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            dispatch(clearValidationErrors());
        }
        if (businessLogicError && Object.keys(businessLogicError).length > 0) {
            setErrors(businessLogicError);
            dispatch(clearBusinessLogicError());
        }
    }, [validationErrors, successMessage, businessLogicError]);

    // HELPER FNS
    const loadingError = () => {
        if (genderDetails?.length < 1) return GENDER_ERROR;
        if (arrayGenders?.length < 1) return 'No genders available for this contract.';
        if (Object.keys(participantContractDetails)?.length < 1) return CONTRACT_DETAILS_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const resetFields = () => {
        updateInitialEntry();
        setErrors({});
        setIsButtonDisabled(false);
    };

    const configUniqueIdLabel = (repeat = '') => {
        return currentParticipant.uniqueIdentifierType === 'NINO'
            ? `NINO ${repeat} Unique ID`
            : currentParticipant.uniqueIdentifierType === 'NHS_NUMBER'
              ? `NHS Number ${repeat} Unique ID`
              : `${repeat} Unique ID`;
    };

    const updateInitialEntry = () => {
        if (currentParticipant?.id) {
            setNewEntry({
                ...currentParticipant,
                repeatUniqueIdentifier: currentParticipant.uniqueIdentifier
            });
            setInitialStateOnEntry({
                ...currentParticipant,
                repeatUniqueIdentifier: currentParticipant.uniqueIdentifier
            });
        }
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateUniqueIdentifier = (key, value) => {
        clearError(key);
        if (value == null) value = '';
        const additionalChanges = {
            uniqueIdentifierType:
                value === 'TEMPORARY' &&
                (newEntry.uniqueIdentifier === 'TEMPORARY' ||
                    newEntry.repeatUniqueIdentifier === 'TEMPORARY')
                    ? 'TEMPORARY'
                    : participantContractDetails.unique_identifier_type.name
        };
        setNewEntry((prev) => ({ ...prev, ...additionalChanges, [key]: value }));
    };

    const onCancel = () => {
        resetFields();
        onClose('general');
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, initialStateOnEntry);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateParticipant(newEntry, _MESSAGE));
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return Object.keys(currentParticipant).length < 1 ? null : (
        <div className={local.generalSection}>
            <form
                className={form.form}
                onSubmit={onSubmit}
                data-testid={'appointments-general-form'}>
                <h2 className={app.sectionHeading}>Participant Details</h2>

                <h2 className={app.sectionHeading}>General Details</h2>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="genderId"
                            label="Gender"
                            placeholder="Select gender..."
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={arrayGenders || []}
                            selectedId={newEntry.genderId || ''}
                            selected={getObjFromId(arrayGenders, newEntry.genderId)}
                            error={errors.genderId}
                            onChange={(chosenId) => onUpdate('genderId', chosenId)}
                        />
                        <DateSelect
                            id="dateOfBirth"
                            label="Date of Birth"
                            disabled={isDisabled}
                            mandatory={true}
                            value={newEntry.dateOfBirth}
                            error={errors.dateOfBirth}
                            onDateChange={(date) => onUpdate('dateOfBirth', date)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <TextInputField
                            id="uniqueId"
                            label={configUniqueIdLabel()}
                            placeholder="Enter unique identifier"
                            disabled={isUniqueIdEditDisabled}
                            mandatory={true}
                            value={newEntry.uniqueIdentifier || ''}
                            error={errors.uniqueIdentifier}
                            onPaste={(e) => e.preventDefault()}
                            onChange={(e) =>
                                onUpdateUniqueIdentifier('uniqueIdentifier', e.target.value)
                            }
                        />
                        <TextInputField
                            id="repeatUniqueId"
                            label={configUniqueIdLabel('Repeat')}
                            placeholder="Repeat unique identifier"
                            disabled={isUniqueIdEditDisabled}
                            mandatory={true}
                            value={newEntry.repeatUniqueIdentifier || ''}
                            error={errors.repeatUniqueIdentifier}
                            onPaste={(e) => e.preventDefault()}
                            onChange={(e) =>
                                onUpdateUniqueIdentifier('repeatUniqueIdentifier', e.target.value)
                            }
                        />
                    </div>
                </div>

                <h2 className={app.sectionHeading}>Contact Details</h2>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextInputField
                            id="address1"
                            label="Address 1"
                            placeholder="Address 1"
                            mandatory={true}
                            disabled={isDisabled}
                            maxLength={ADDRESS_LINE_1_MAX_LENGTH}
                            value={newEntry.address1 || ''}
                            error={errors.address1}
                            onChange={(e) => onUpdate('address1', e.target.value)}
                        />
                        <TextInputField
                            id="address2"
                            label="Address 2"
                            placeholder="Address 2"
                            disabled={isDisabled}
                            maxLength={ADDRESS_LINE_2_MAX_LENGTH}
                            value={newEntry.address2 || ''}
                            error={errors.address2}
                            onChange={(e) => onUpdate('address2', e.target.value)}
                        />
                        <TextInputField
                            id="address3"
                            label="Address 3"
                            placeholder="Address 3"
                            disabled={isDisabled}
                            maxLength={ADDRESS_LINE_3_MAX_LENGTH}
                            value={newEntry.address3 || ''}
                            error={errors.address3}
                            onChange={(e) => onUpdate('address3', e.target.value)}
                        />
                        <TextInputField
                            id="city"
                            label="Town/City"
                            placeholder="Town/City"
                            disabled={isDisabled}
                            maxLength={CITY_MAX_LENGTH}
                            value={newEntry.city || ''}
                            error={errors.city}
                            onChange={(e) => onUpdate('city', e.target.value)}
                        />
                        <TextInputField
                            id="postcode"
                            label="Postcode"
                            placeholder="Postcode"
                            mandatory={true}
                            disabled={isDisabled}
                            maxLength={MAX_POSTCODE}
                            value={newEntry.postcode || ''}
                            error={errors.postcode}
                            onChange={(e) => onUpdate('postcode', e.target.value.toUpperCase())}
                        />
                    </div>

                    <div className={form.formColumn}>
                        <TextInputField
                            id="emailAddress"
                            label="Email Address"
                            placeholder="Email address"
                            disabled={isDisabled}
                            maxLength={MAX_EMAIL}
                            value={newEntry.emailAddress || ''}
                            error={errors.emailAddress}
                            onChange={(e) => onUpdate('emailAddress', e.target.value)}
                        />
                        <TextInputField
                            id="primaryPhoneNumber"
                            label="Primary Phone Number"
                            placeholder="Primary phone number"
                            disabled={isDisabled}
                            maxLength={MAX_PHONE}
                            value={newEntry.primaryPhoneNumber || ''}
                            error={errors.primaryPhoneNumber}
                            onChange={(e) => onUpdate('primaryPhoneNumber', e.target.value)}
                        />
                        <TextInputField
                            id="secondaryPhoneNumber"
                            label="Secondary Phone Number"
                            placeholder="Secondary phone number"
                            disabled={isDisabled}
                            maxLength={MAX_PHONE}
                            value={newEntry.secondaryPhoneNumber || ''}
                            error={errors.secondaryPhoneNumber}
                            onChange={(e) => onUpdate('secondaryPhoneNumber', e.target.value)}
                        />
                    </div>
                </div>
                <div className={local.generalFormActions}>
                    <FormActions
                        onClose={onSubmit}
                        onCancel={onCancel}
                        btnText="Update"
                        disabled={isButtonDisabled || isDisabled}
                        error={errors.button}
                        clearError={() => clearError('button')}
                    />
                    {errors.businessLogicError?.error && (
                        <ScreenError error={errors.businessLogicError} clearError={clearError} />
                    )}
                </div>
            </form>
        </div>
    );
};

General.propTypes = {
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    isUniqueIdEditDisabled: PropTypes.bool
};

export default General;
